import { asDate } from "@@/shared/date-time/as-date";
import {
    dateAgnosticTimeRelaxedMinuteZodSchema,
    type DateAgnosticTime,
} from "@@/shared/date-time/availability/date-agnostic-time";
import type { Timezone} from "@@/shared/date-time/timezone";
import { timezoneZodType, tz } from "@@/shared/date-time/timezone";
import type { OrderItem_V1 } from "@@/shared/orders/order-items/order-item-v1";
import type { OrderItem_V2 } from "@@/shared/orders/order-items/order-item-v2";
import { addHours, addMinutes, isWithinInterval, startOfDay } from "date-fns";
import { toZonedTime } from "date-fns-tz";
import { z } from "zod";
import type { DiscountCondition } from "./discount-condition";
import {
    createDiscountConditionId,
    type DiscountConditionId,
} from "./discount-condition-id";
import type { DiscountConditionValidator } from "./discount-condition-validator";

const timeOfDayConditionType = "TIME_OF_DAY_CONDITION";
type TimeOfDayConditionType = typeof timeOfDayConditionType;

type TimeOfDayCondition = {
    _id: DiscountConditionId;
    type: TimeOfDayConditionType;
    from: DateAgnosticTime;
    to: DateAgnosticTime;
    timezone?: Timezone;
    strict: boolean;
};

const createTimeOfDayCondition = (
    from: DateAgnosticTime,
    to: DateAgnosticTime,
    strict: boolean,
    timezone?: Timezone,
): TimeOfDayCondition => {
    return {
        _id: createDiscountConditionId(),
        type: "TIME_OF_DAY_CONDITION",
        from,
        to,
        strict,
        timezone,
    };
};

const getTimeOfDayConditionValidator =
    <TOrderItem extends OrderItem_V1 | OrderItem_V2 = OrderItem_V2>(
        condition: TimeOfDayCondition,
    ): DiscountConditionValidator<TOrderItem> =>
    (items: TOrderItem[]) => {
        const output = items.filter(item => {
            if (item.acquireType === "BOOKABLE") {
                const sessionStart = toZonedTime(
                    asDate(item.time.start),
                    condition.timezone ?? tz.EuropeStockholm,
                );
                const sessionEnd = toZonedTime(
                    asDate(item.time.end),
                    condition.timezone ?? tz.EuropeStockholm,
                );

                const startOf = startOfDay(sessionStart);

                const from = addMinutes(
                    addHours(startOf, condition.from.hour),
                    condition.from.minute,
                );
                const to = addMinutes(
                    addHours(startOf, condition.to.hour),
                    condition.to.minute,
                );
                if (condition.strict) {
                    return (
                        isWithinInterval(sessionStart, {
                            start: from,
                            end: to,
                        }) &&
                        isWithinInterval(sessionEnd, {
                            start: from,
                            end: to,
                        })
                    );
                } else {
                    return isWithinInterval(sessionStart, {
                        start: from,
                        end: to,
                    });
                }
            }
            return false;
        });
        return output;
    };

const timeOfDayConditionZodSchema: z.ZodSchema<TimeOfDayCondition> = z.object({
    _id: z.string() as unknown as z.ZodSchema<DiscountConditionId>,
    type: z.literal("TIME_OF_DAY_CONDITION"),
    from: dateAgnosticTimeRelaxedMinuteZodSchema,
    to: dateAgnosticTimeRelaxedMinuteZodSchema,
    strict: z.boolean(),
    timezone: timezoneZodType.optional(),
});

const isTimeOfDayCondition = (
    condition: DiscountCondition,
): condition is TimeOfDayCondition => {
    return condition.type === timeOfDayConditionType;
};

export {
    createTimeOfDayCondition,
    getTimeOfDayConditionValidator,
    isTimeOfDayCondition,
    timeOfDayConditionType,
    timeOfDayConditionZodSchema,
};
export type { TimeOfDayCondition, TimeOfDayConditionType };
